import React from 'react'
import { Link, router } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TableRow, TableCell } from '@/components/ui/table'
import { RotateCw, Loader2 } from 'lucide-react'
import { formatMoney } from '@/lib/format'

// InvoiceRow (FE2) — one row of the in-house invoice history. Pure display: the period,
// the total via the shared `formatMoney` (NEVER recomputed — R-FE-MATH), a status badge,
// and a retry-charge button when the invoice failed. The total arrives pre-decimalized
// from the backend (Invoice#total_amount#to_f); this component does ZERO money math.

export interface InvoiceRowData {
  id: number
  invoiceNumber: string
  periodStartOn: string
  periodEndOn: string
  subtotal: number
  vat: number
  total: number
  currency: string
  status: string
  issuedAt: string | null
  retryable: boolean
  /**
   * WHICH PAYMENT METHOD this document is addressed to, composed by the backend
   * (Operator::BillingController#invoice_rows). A cycle emits one whole invoice per payment
   * method (billing-payers §2 decision 3), so on a business with more than one the number alone
   * does not say whose card settles it. Rendered ONLY when `showPayer` is on — a single-payment-
   * method business must see exactly the table it always saw.
   */
  payerName?: string | null
  /**
   * WHAT KIND OF DOCUMENT this is, when it is not the ordinary kind: "Correction" for a correction
   * invoice (billing-payers §13), which shares its period with a paid invoice. Backend-composed and
   * rendered verbatim under the number; absent on every other row, which renders as it always did.
   */
  kindLabel?: string | null
  /**
   * THIS DOCUMENT'S OWN PAGE (billing-payers §11.3), served whole by the backend. An invoice is a
   * legal document and until it existed the operator could not open one. Optional: a row without a
   * path renders the number as plain text rather than the page inventing a URL for it.
   */
  href?: string | null
}

// Status → tone, on the locked design-system semantics (emerald=paid, amber=open,
// rose=failed, slate=draft).
const STATUS_TONE: Record<string, string> = {
  paid: 'bg-emerald-100 text-emerald-700',
  issued: 'bg-amber-100 text-amber-700',
  failed: 'bg-rose-100 text-rose-700',
  draft: 'bg-slate-100 text-slate-700',
}

// A backend ISO date rendered for display only — no client-side date math.
function formatPeriod(startIso: string, endIso: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  const start = new Date(startIso).toLocaleDateString('en-GB', opts)
  const end = new Date(endIso).toLocaleDateString('en-GB', opts)
  return `${start} – ${end}`
}

// `settingsPath` is the business's backend-served settings root (`currentAccount.settingsPath`);
// the retry endpoint is that plus a fixed suffix and the invoice's own id.
export default function InvoiceRow({
  invoice,
  settingsPath,
  showPayer = false,
}: {
  invoice: InvoiceRowData
  settingsPath: string
  showPayer?: boolean
}) {
  const [retrying, setRetrying] = React.useState(false)
  const tone = STATUS_TONE[invoice.status] ?? STATUS_TONE.draft

  function retry() {
    setRetrying(true)
    // State-changing action (a re-charge), not navigation → router.post, never <Link>.
    router.post(`${settingsPath}/billing/invoices/${invoice.id}/retry`, {}, { onFinish: () => setRetrying(false) })
  }

  return (
    <TableRow>
      <TableCell className="font-medium tabular-nums text-foreground">
        {/* Opening a document is navigation, so it is a <Link> (an <a>), never a button. */}
        {invoice.href ? (
          <Link href={invoice.href} className="transition-colors hover:text-primary hover:underline">
            {invoice.invoiceNumber}
          </Link>
        ) : (
          invoice.invoiceNumber
        )}
        {invoice.kindLabel && (
          <span className="block text-[11px] font-normal text-muted-foreground">{invoice.kindLabel}</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatPeriod(invoice.periodStartOn, invoice.periodEndOn)}
      </TableCell>
      {showPayer && (
        <TableCell className="text-sm text-muted-foreground">{invoice.payerName ?? '—'}</TableCell>
      )}
      <TableCell className="text-right tabular-nums font-semibold text-foreground">
        {formatMoney(invoice.total, invoice.currency)}
        {/* The VAT sub-line renders ONLY when the invoice actually carries VAT (display-only
            conditional on the backend-sent amount — no frontend math, R-FE-MATH). While
            zewzew is unregistered every invoice has vat 0 and shows no VAT surface at all;
            post-registration invoices show it for B2B input-VAT reclaim, verbatim. */}
        {invoice.vat > 0 && (
          <span className="block text-[11px] font-normal text-muted-foreground">
            incl. {formatMoney(invoice.vat, invoice.currency)} VAT
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Badge variant="outline" className={`text-[11px] capitalize ${tone}`}>
          {invoice.status}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        {invoice.retryable ? (
          <Button
            onClick={retry}
            disabled={retrying}
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg text-rose-500 hover:text-rose-600"
          >
            {retrying ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCw className="mr-1.5 h-3.5 w-3.5" />
            )}
            Retry
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground/60">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}
